/**
 * «کارت‌های دستگاه» — هر ترددِ فایل یک جای مشخص دارد (v2.110.1).
 *
 * کاربر (۱۴۰۵/۰۷/۰۲): «نمی‌شه از فایل یه چیزی بیاد ولی مشخص نباشه چیا بوده؛ یه جا بذار برای این
 * بی‌نام‌ونشون‌ها که بتونیم دستی اضافه یا حذفشون کنیم».
 *
 * همهٔ کارت‌های ماه با وضعیت: بی‌صاحب، کارتِ مشترک، پروندهٔ غیرفعال، بدونِ تیکِ حضور، سالم.
 * «انتقال» ترددهای یک کارت (از یک تاریخ) را به پرسنلِ درست می‌برد؛ «ترددها» ریزِ کارت را با حذف/افزودنِ
 * تک‌تردد نشان می‌دهد. روزهای حضور بلافاصله از روی ترددها ساخته می‌شوند؛ ویرایش‌های دستیِ روز دست نمی‌خورد.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Checkbox, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag,
  Tooltip, Typography, message,
} from "antd";
import { DeleteOutlined, PlusOutlined, ReloadOutlined, SwapOutlined } from "@ant-design/icons";
import GxTable from "../../components/GxTable";
import GxNote from "../../components/GxNote";
import {
  addRawPunch, deleteRawPunch, getDeviceCardPunches, getDeviceCards, moveDeviceCard,
} from "../../api/client";
import { JalaliDateInput, toJalali } from "../../utils/dateUtils";
import { fa } from "./attendanceMeta";

const { Text } = Typography;

const STATE = {
  unmapped: ["بی‌صاحب", "red", "به هیچ پرسنلی نرسیده"],
  conflict: ["کارتِ مشترک", "volcano", "این شماره روی پروندهٔ پرسنلِ دیگری هم ثبت است؛ ترددها فقط به یکی رسیده"],
  inactive: ["پروندهٔ غیرفعال", "orange", "ترددها به پرونده‌ای رسیده که غیرفعال است ⇒ در حضور دیده نمی‌شود"],
  not_tracked: ["بدونِ تیکِ حضور", "gold", "صاحبِ کارت تیکِ «حضور و غیاب دستگاه» ندارد ⇒ در حضور دیده نمی‌شود"],
  ok: ["سالم", "green", "به پرسنلِ فعال و حضوری رسیده"],
};

export default function DeviceCardsTab({ year, month, employees = [], onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [move, setMove] = useState(null);          // کارتِ در حالِ انتقال
  const [open, setOpen] = useState(null);          // کارتی که ترددهایش باز است
  const [punches, setPunches] = useState([]);
  const [add, setAdd] = useState({ date: null, time: "" });
  const [preview, setPreview] = useState(null);    // پیش‌نمایشِ انتقال (بدونِ ذخیره)

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await getDeviceCards(year, month)).data.rows || []); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در بارگذاری کارت‌ها"); }
    setLoading(false);
  }, [year, month]);
  useEffect(() => { load(); setOpen(null); }, [load]);

  const loadPunches = useCallback(async (uid) => {
    try { setPunches((await getDeviceCardPunches(uid, year, month)).data.rows || []); }
    catch { setPunches([]); }
  }, [year, month]);
  useEffect(() => { if (open) loadPunches(open.device_uid); }, [open, loadPunches]);

  const empOpts = useMemo(() => employees.map((e) => ({
    value: e.id, label: `${e.full_name}${e.employee_code ? ` (${e.employee_code})` : ""}${e.status === "inactive" ? " — غیرفعال" : ""}`,
  })), [employees]);

  const shown = onlyProblems ? rows.filter((r) => r.state !== "ok") : rows;
  const refreshAll = async () => { await load(); if (open) await loadPunches(open.device_uid); onChanged?.(); };

  const startMove = (r) => {
    // پیشنهاد: پرسنلِ فعال و حضوریِ دیگری که همین کارت روی پرونده‌اش است، از روزِ ورودش
    const cand = (r.also_on || []).find((o) => o.active && o.attendance_required) || r.suggest
      || (r.also_on || [])[0];
    setMove({ card: r, employee_id: cand?.id ?? null, from_date: cand?.hire_date ?? null,
              set_card: true, clear_others: true });
  };
  // هر تغییرِ مقصد/تاریخ ⇒ «چند تردد از چند روز جابه‌جا می‌شود» پیش از تأیید
  useEffect(() => {
    if (!move?.employee_id) { setPreview(null); return; }
    let live = true;
    moveDeviceCard(move.card.device_uid, {
      employee_id: move.employee_id, from_date: move.from_date || null,
      set_card: move.set_card, clear_others: move.clear_others,
    }, true).then((r) => live && setPreview(r.data)).catch(() => live && setPreview(null));
    return () => { live = false; };
  }, [move]);

  const doMove = async () => {
    if (!move.employee_id) return message.warning("پرسنلِ مقصد را انتخاب کنید");
    try {
      const r = await moveDeviceCard(move.card.device_uid, {
        employee_id: move.employee_id, from_date: move.from_date || null,
        set_card: move.set_card, clear_others: move.clear_others,
      });
      message.success(r.data.message);
      setMove(null); await refreshAll();
    } catch (e) { message.error(e?.response?.data?.detail || "انتقال انجام نشد"); }
  };
  const doAdd = async () => {
    if (!open?.employee_id) return message.warning("اول کارت را به یک پرسنل منتقل کنید");
    if (!add.date || !add.time) return message.warning("تاریخ و ساعت لازم است");
    try {
      await addRawPunch({ employee_id: open.employee_id, device_uid: open.device_uid,
        work_date: add.date, punch_time: add.time });
      message.success("تردد ثبت شد"); setAdd({ date: add.date, time: "" }); await refreshAll();
    } catch (e) { message.error(e?.response?.data?.detail || "ثبت نشد"); }
  };

  const columns = [
    { title: "کارت", dataIndex: "device_uid", width: 80, align: "center",
      render: (v) => <Tag style={{ fontSize: 13 }}>{fa(v)}</Tag> },
    { title: "وضعیت", dataIndex: "state", width: 140, gxFilter: "enum",
      gxFilterGet: (r) => STATE[r.state]?.[0],
      render: (v) => <Tooltip title={STATE[v]?.[2]}><Tag color={STATE[v]?.[1]}>{STATE[v]?.[0]}</Tag></Tooltip> },
    { title: "ترددها رسیده به", dataIndex: "employee_name", width: 200,
      render: (v, r) => (v
        ? <span>{v}{r.employee_code ? <Text type="secondary"> ({fa(r.employee_code)})</Text> : null}</span>
        : <Text type="secondary">{r.label ? `«${r.label}» (برچسب)` : "— هیچ‌کس —"}</Text>) },
    { title: "پیشنهاد", key: "suggest", width: 170,
      render: (_, r) => (r.suggest
        ? <Tag color="blue">پروندهٔ فعال: {r.suggest.full_name}{r.suggest.employee_code ? ` (${fa(r.suggest.employee_code)})` : ""}</Tag>
        : "—") },
    { title: "این کارت روی پروندهٔ", key: "also_on", width: 240,
      render: (_, r) => ((r.also_on || []).length ? (
        <Space direction="vertical" size={0}>
          {r.also_on.map((o) => (
            <span key={o.id} style={{ fontSize: 12 }}>
              {o.full_name} {o.employee_code ? `(${fa(o.employee_code)})` : ""}
              {!o.active && <Tag color="orange" style={{ marginInlineStart: 4 }}>غیرفعال</Tag>}
              {o.active && !o.attendance_required && <Tag style={{ marginInlineStart: 4 }}>غیرحضوری</Tag>}
              {o.hire_date && <Text type="secondary"> — ورود {toJalali(o.hire_date)}</Text>}
            </span>
          ))}
        </Space>) : "—") },
    { title: "تردد", dataIndex: "punches", width: 70, align: "center", render: (v) => fa(v) },
    { title: "بازه", key: "range", width: 170,
      render: (_, r) => <span className="gx-time">{toJalali(r.first)} تا {toJalali(r.last)}</span> },
    { title: "", key: "act", width: 200,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<SwapOutlined />} type={r.state === "ok" ? "default" : "primary"}
            onClick={() => startMove(r)}>انتقال…</Button>
          <Button size="small" onClick={() => setOpen(open?.device_uid === r.device_uid ? null : r)}>
            {open?.device_uid === r.device_uid ? "بستن" : "ترددها"}
          </Button>
        </Space>) },
  ];

  return (
    <Card title={`کارت‌های دستگاه — هر تردد کجا رفته؟`}
      extra={<Space>
        <Switch size="small" checked={onlyProblems} onChange={setOnlyProblems} />
        <Text style={{ fontSize: 12 }}>فقط مشکل‌دارها</Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
      </Space>}>
      <GxNote id="att-device-cards" signature="v1"
        message="هیچ ترددی بی‌صاحب یا گم نمی‌ماند: کارت‌هایی که به کسی نرسیده، به پروندهٔ غیرفعال/غیرحضوری رسیده یا بینِ دو نفر مشترک است اینجاست">
        «انتقال…» ترددهای کارت را (از تاریخی که می‌دهید) به پرسنلِ درست می‌برد و کارت را روی پرونده‌اش ثبت می‌کند تا
        ایمپورت‌های بعدی خودکار درست بیاید. «ترددها» ریزِ کارت را نشان می‌دهد و تک‌تردد را حذف یا اضافه می‌کنید.
        روزهای حضور همان لحظه از روی ترددها ساخته می‌شوند؛ ورود/خروجی که دستی اصلاح کرده‌اید دست نمی‌خورد.
      </GxNote>
      <GxTable layoutId="att-device-cards" dataSource={shown} columns={columns} rowKey={(r) => `${r.device_uid}-${r.employee_id}`}
        size="small" loading={loading} pagination={false} scroll={{ x: "max-content" }}
        locale={{ emptyText: onlyProblems ? "همهٔ کارت‌های این ماه سالم‌اند" : "ترددی نیست" }} />

      {open && (
        <Card size="small" style={{ marginTop: 12 }}
          title={<span>ترددهای کارتِ {fa(open.device_uid)} — {open.employee_name || "بی‌صاحب"}</span>}>
          <Space wrap style={{ marginBottom: 8 }}>
            <Text style={{ fontSize: 12 }}>افزودنِ تردد:</Text>
            <JalaliDateInput value={add.date} onChange={(v) => setAdd({ ...add, date: v })} />
            <Input value={add.time} onChange={(e) => setAdd({ ...add, time: e.target.value })}
              placeholder="۰۸:۰۵" style={{ width: 90 }} />
            <Button size="small" icon={<PlusOutlined />} onClick={doAdd} disabled={!open.employee_id}>ثبت</Button>
            {!open.employee_id && <Text type="warning" style={{ fontSize: 12 }}>برای افزودن، اول کارت را به یک پرسنل منتقل کنید.</Text>}
          </Space>
          <Table size="small" rowKey="id" dataSource={punches} pagination={{ pageSize: 40, size: "small" }}
            columns={[
              { title: "تاریخ", dataIndex: "work_date", width: 110, render: (v) => toJalali(v) },
              { title: "ساعت", dataIndex: "punch_time", width: 80, render: (v) => <span className="gx-time">{v}</span> },
              { title: "رسیده به", dataIndex: "employee_name", render: (v) => v || "—" },
              { title: "منبع", dataIndex: "source_file", width: 140,
                render: (v, r) => (r.source === "manual" ? <Tag color="purple">دستی</Tag> : (v || "—")) },
              { title: "", key: "del", width: 50,
                render: (_, p) => (
                  <Popconfirm title="این تردد حذف شود؟" okText="حذف" cancelText="انصراف"
                    okButtonProps={{ danger: true }}
                    onConfirm={async () => {
                      try { await deleteRawPunch(p.id); message.success("حذف شد"); await refreshAll(); }
                      catch (e) { message.error(e?.response?.data?.detail || "حذف نشد"); }
                    }}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>) },
            ]} />
        </Card>
      )}

      <Modal open={!!move} title={`انتقالِ ترددهای کارتِ ${move ? fa(move.card.device_uid) : ""}`}
        onCancel={() => setMove(null)} onOk={doMove} okText="انتقال" cancelText="انصراف" destroyOnHidden>
        {move && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ fontSize: 12, color: "#888" }}>
              الان به: <b>{move.card.employee_name || "هیچ‌کس"}</b> — {fa(move.card.punches)} تردد در این ماه
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>به پرسنلِ</Text>
              <Select showSearch optionFilterProp="label" style={{ width: "100%" }} options={empOpts}
                value={move.employee_id} onChange={(v) => setMove({ ...move, employee_id: v })} />
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>فقط ترددهای این تاریخ به بعد (خالی = همه)</Text>
              <div><JalaliDateInput value={move.from_date} onChange={(v) => setMove({ ...move, from_date: v })} /></div>
            </div>
            <Checkbox checked={move.set_card} onChange={(e) => setMove({ ...move, set_card: e.target.checked })}>
              کارتِ {fa(move.card.device_uid)} روی پروندهٔ او ثبت شود (ایمپورت‌های بعدی خودکار)
            </Checkbox>
            <Checkbox checked={move.clear_others} onChange={(e) => setMove({ ...move, clear_others: e.target.checked })}>
              این شماره از پرونده‌های دیگر پاک شود (کارتِ مشترک نماند)
            </Checkbox>
            {preview && (
              <div style={{ background: "var(--gx-surface-2, #fafafa)", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
                با این انتخاب <b>{fa(preview.moved)}</b> تردد در <b>{fa(preview.days)}</b> روز جابه‌جا می‌شود
                {move.from_date ? ` (از ${toJalali(move.from_date)})` : " (همهٔ سابقهٔ کارت، همهٔ ماه‌ها)"}
                {preview.cleared_card_from?.length > 0 && <> — کارت از «{preview.cleared_card_from.join("، ")}» برداشته می‌شود</>}.
              </div>
            )}
          </Space>
        )}
      </Modal>
    </Card>
  );
}
